import React, { useEffect, useState } from "react";
import { fetchTournamentOptions, canEditRecord } from "./PoolModalHelper";
import { MdErrorOutline } from "react-icons/md";
import moment from "moment";
import { supabase } from "../ReusableComponents/supabaseClient";
import { toast } from "react-toastify";
import { logError } from "../Utilities/errorLogger";

const inputClass = "w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-gray-900 bg-white disabled:bg-gray-50 disabled:text-gray-400";
const inputErrorClass = "w-full px-4 py-3 rounded-xl border-2 border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all text-gray-900 bg-red-50";
const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";

const PoolForm = ({ poolData, mode, setFormData, canEdit, setCanEdit, activeTab, errors, setErrors }) => {
  const [formState, setFormState] = useState({
    poolName: poolData?.name || "",
    tournament: poolData?.tournament_fixture?.id || "",
    teamSize: poolData?.max_players || "5",
    numPlayersScored: poolData?.num_player_used_for_scoring || "5",
    allMakeCut: poolData?.all_players_must_make_cut || false,
    tieBreaker: poolData?.tie_breaker || "",
    poolId: poolData?.id || null,
    tour: poolData?.tour_fixtures || "",
  });

  const [tournaments, setTournaments] = useState([]);
  const [tours, setTours] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingTournamentChange, setPendingTournamentChange] = useState(null);
  const [selectedTournament, setSelectedTournament] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const { data: toursData, error: toursError } = await supabase.from("tourFixture").select("id, tour_id, tour_name").order("tour_name", { ascending: true });
        if (toursError) throw toursError;

        const pgaTour = toursData.find((t) => t.tour_id === 1) || toursData[0];
        setTours(toursData);

        // Set default tour to PGA Tour (or first available)
        const currentTourId = formState.tour ? parseInt(formState.tour) : null;
        const defaultTour = currentTourId ? toursData.find((t) => t.id === currentTourId) : pgaTour;

        if (!formState.tour && defaultTour) {
          setFormState((prev) => ({ ...prev, tour: defaultTour.id.toString() }));
        }

        const selectedTour = defaultTour || pgaTour;
        if (selectedTour) {
          const tournamentData = await fetchTournamentOptions(poolData?.tournament_fixture?.tournament_id, selectedTour.tour_id);
          setTournaments(tournamentData);

          if (poolData?.tournament_fixture?.tournament_id) {
            const matchingTournament = tournamentData.find((t) => t.tournament_id === poolData.tournament_fixture.tournament_id);
            if (matchingTournament) {
              setSelectedTournament(matchingTournament);
              setFormState((prev) => ({ ...prev, tournament: matchingTournament.id }));
            }
          }
        }
      } catch (error) {
        await logError({ filePath: 'Pool/PoolForm.js', functionName: 'loadData', errorType: 'Data Loading Error', errorMessage: error.message, stackTrace: error.stack });
      } finally {
        setIsLoading(false);
      }
    };

    if (activeTab === "edit" || !activeTab) loadData();
  }, [activeTab, poolData?.tournament_fixture?.tournament_id, formState.tour]);

  useEffect(() => {
    if (mode === "edit" && poolData) {
      const canEditValue = canEditRecord(poolData);
      if (selectedTournament) {
        const poolWithSelected = { ...poolData, tournament_fixture: { ...poolData.tournament_fixture, ...selectedTournament }, first_tee_time: selectedTournament.first_tee_time, time_zone: selectedTournament.time_zone };
        setCanEdit(canEditValue && canEditRecord(poolWithSelected));
      } else {
        setCanEdit(canEditValue);
      }
    }
  }, [selectedTournament, mode, poolData, setCanEdit]);

  useEffect(() => { if (setFormData) setFormData(formState); }, [formState, setFormData]);

  const handleInputChange = async (e) => {
    if (mode === "edit" && !canEdit) { e.preventDefault(); return; }

    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    if (type === "number") {
      const numValue = parseInt(value);
      if (numValue < 1) newValue = "1";
      else if (numValue > 15) newValue = "15";
    }

    if (name === "tournament" && mode === "edit" && poolData?.id) {
      const tournament = tournaments.find(t => t.id === parseInt(value));
      if (tournament) {
        const poolWithNew = { ...poolData, tournament_fixture: { ...poolData.tournament_fixture, ...tournament }, first_tee_time: tournament.first_tee_time, time_zone: tournament.time_zone };
        if (!canEditRecord(poolWithNew)) {
          toast.error("Cannot change to a tournament that has already started", { theme: "dark" });
          return;
        }
        setPendingTournamentChange(newValue);
        setShowConfirmation(true);
        return;
      }
    }

    // Auto-update pool name when tournament changes (if name is empty or still matches old tournament)
    if (name === "tournament") {
      const newTournament = tournaments.find(t => t.id === parseInt(newValue));
      const oldTournament = tournaments.find(t => t.id === parseInt(formState.tournament));
      setFormState((prev) => {
        const shouldAutoUpdate = !prev.poolName || prev.poolName === oldTournament?.name;
        return { ...prev, [name]: newValue, ...(shouldAutoUpdate && newTournament ? { poolName: newTournament.name } : {}) };
      });
    } else {
      setFormState((prev) => ({ ...prev, [name]: newValue }));
    }
    if (errors && errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleConfirmTournamentChange = async () => {
    try {
      const { error: deleteError } = await supabase.from('teamLineItem').delete().eq('pool', poolData.id);
      if (deleteError) throw deleteError;
      const newTournament = tournaments.find(t => t.id === parseInt(pendingTournamentChange));
      const oldTournament = tournaments.find(t => t.id === parseInt(formState.tournament));
      setFormState((prev) => {
        const shouldAutoUpdate = !prev.poolName || prev.poolName === oldTournament?.name;
        return { ...prev, tournament: pendingTournamentChange, ...(shouldAutoUpdate && newTournament ? { poolName: newTournament.name } : {}) };
      });
      toast.success("Tournament changed and team selections cleared", { theme: "dark" });
    } catch (error) {
      await logError({ filePath: 'Pool/PoolForm.js', functionName: 'handleConfirmTournamentChange', errorType: 'Tournament Change Error', errorMessage: error.message, stackTrace: error.stack });
      toast.error("Failed to change tournament", { theme: "dark" });
    } finally {
      setShowConfirmation(false);
      setPendingTournamentChange(null);
    }
  };

  return (
    <div className="px-6 py-4">
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {mode === "edit" && !canEdit && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-center">
              <p className="text-amber-800 text-sm font-medium">Tournament has started. Editing is disabled.</p>
            </div>
          )}

          <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
            {/* Tour */}
            <div>
              <label className={labelClass} htmlFor="tour">Tour</label>
              <select id="tour" name="tour" value={formState.tour} onChange={handleInputChange} disabled={mode === "edit" && !canEdit} className={errors?.tour ? inputErrorClass : inputClass}>
                {tours.map((tour) => (<option key={tour.id} value={tour.id}>{tour.tour_name}</option>))}
              </select>
              {errors?.tour && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><MdErrorOutline className="w-4 h-4" />{errors.tour}</p>}
            </div>

            {/* Tournament */}
            <div>
              <label className={labelClass} htmlFor="tournament">Tournament</label>
              <select id="tournament" name="tournament" value={formState.tournament} onChange={handleInputChange} disabled={mode === "edit" && !canEdit} className={errors?.tournament ? inputErrorClass : inputClass}>
                <option value="">Select Tournament</option>
                {tournaments.map((t) => (<option key={t.id} value={t.id}>{t.name} - {moment(t.start_date).format("MM/DD/YY")}</option>))}
              </select>
              {errors?.tournament && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><MdErrorOutline className="w-4 h-4" />{errors.tournament}</p>}
            </div>

            {/* Pool Name */}
            <div>
              <label className={labelClass} htmlFor="poolName">Pool Name</label>
              <input type="text" id="poolName" name="poolName" value={formState.poolName} onChange={handleInputChange} disabled={mode === "edit" && !canEdit} maxLength={100} placeholder="e.g., Masters 2026 - Friends Pool" className={errors?.poolName ? inputErrorClass : inputClass} />
              {errors?.poolName && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><MdErrorOutline className="w-4 h-4" />{errors.poolName}</p>}
            </div>

            {/* Players Per Team */}
            <div>
              <label className={labelClass} htmlFor="teamSize">Players Per Team</label>
              <input type="number" id="teamSize" name="teamSize" value={formState.teamSize} onChange={handleInputChange} disabled={mode === "edit" && !canEdit} min="1" max="15" className={errors?.teamSize ? inputErrorClass : inputClass} />
              {errors?.teamSize && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><MdErrorOutline className="w-4 h-4" />{errors.teamSize}</p>}
            </div>

            {/* Players Scored */}
            <div>
              <label className={labelClass} htmlFor="numPlayersScored">Players Used For Scoring</label>
              <input type="number" id="numPlayersScored" name="numPlayersScored" value={formState.numPlayersScored} onChange={handleInputChange} disabled={mode === "edit" && !canEdit} min="1" max="15" className={errors?.numPlayersScored ? inputErrorClass : inputClass} />
              {errors?.numPlayersScored && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><MdErrorOutline className="w-4 h-4" />{errors.numPlayersScored}</p>}
            </div>

            {/* Tie Breaker */}
            <div>
              <label className={labelClass} htmlFor="tieBreaker">Tie Breaker</label>
              <select id="tieBreaker" name="tieBreaker" value={formState.tieBreaker} onChange={handleInputChange} disabled={mode === "edit" && !canEdit} className={errors?.tieBreaker ? inputErrorClass : inputClass}>
                <option value="">Select Tie Breaker</option>
                <option value="Average World Ranking">Average World Ranking</option>
                <option value="Winning Score">Winning Score</option>
              </select>
              {errors?.tieBreaker && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><MdErrorOutline className="w-4 h-4" />{errors.tieBreaker}</p>}
            </div>

            {/* All Make Cut */}
            <div className="flex items-center gap-3 pt-2">
              <input type="checkbox" id="allMakeCut" name="allMakeCut" checked={formState.allMakeCut} onChange={handleInputChange} disabled={mode === "edit" && !canEdit} className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary" />
              <label htmlFor="allMakeCut" className="text-sm text-gray-700">Require all players to make the cut to be eligible for scoring</label>
            </div>
          </form>

          {/* Confirmation Dialog */}
          {showConfirmation && (
            <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Change Tournament?</h3>
                <p className="text-sm text-gray-600 mb-6">Changing the tournament will delete any team player selections. Are you sure?</p>
                <div className="flex gap-3">
                  <button onClick={() => { setShowConfirmation(false); setPendingTournamentChange(null); }} className="flex-1 py-2.5 px-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-all text-sm">Cancel</button>
                  <button onClick={handleConfirmTournamentChange} className="flex-1 py-2.5 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold transition-all text-sm">Change</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PoolForm;
